// services/calendarService.ts

interface GoogleCalendarEvent {
  id?: string;
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
  reminders?: {
    useDefault: boolean;
    overrides?: Array<{
      method: 'email' | 'popup';
      minutes: number;
    }>;
  };
  // Add other properties as needed
}

interface GoogleCalendarEventsResponse {
  items: GoogleCalendarEvent[];
  // Add other response properties as needed
}

export const createCalendarEvent = async (
  accessToken: string,
  eventData: GoogleCalendarEvent
): Promise<GoogleCalendarEvent> => {
  try {
    const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Error al crear el evento');
    }

    return await response.json() as GoogleCalendarEvent;
  } catch (error) {
    console.error('Error en createCalendarEvent:', error);
    throw new Error(
      error instanceof Error 
        ? error.message 
        : 'Ocurrió un error desconocido al crear el evento'
    );
  }
};

export const getCalendarEvents = async (
  accessToken: string
): Promise<GoogleCalendarEventsResponse> => {
  try {
    const now = new Date().toISOString();
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events?` +
      `timeMin=${now}&singleEvents=true&orderBy=startTime`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Error al obtener eventos');
    }

    return await response.json() as GoogleCalendarEventsResponse;
  } catch (error) {
    console.error('Error en getCalendarEvents:', error);
    throw new Error(
      error instanceof Error 
        ? error.message 
        : 'Ocurrió un error desconocido al obtener eventos'
    );
  }
};

// Additional utility functions
export const formatEventDateTime = (
  date: Date,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): { dateTime: string; timeZone: string } => {
  return {
    dateTime: date.toISOString(),
    timeZone
  };
};